import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Shared look for the profile page's cards: one white card style, one
 * section header (tinted icon tile, title, subtitle, optional action).
 */

// Archivo is loaded in public/index.html; used for names, titles and numbers.
export const DISPLAY_FONT = '"Archivo", "Helvetica Neue", Arial, sans-serif';

export const PROFILE_COLORS = {
  page: '#F3F5F9',
  ink: '#16181D',
  body: '#3A4150',
  muted: '#5B6270',
  border: '#E4E7EC',
  divider: '#EEF0F3',
  subtle: '#F3F5F9',
  blue: '#1877F2',
  blueDark: '#0F5BCC',
  blueTint: '#E8F1FE',
  blueText: '#0B4FB3',
} as const;

export const profileCardSx = {
  bgcolor: 'white',
  border: `1px solid ${PROFILE_COLORS.border}`,
  borderRadius: '20px',
  boxShadow: 'none',
  p: { xs: 2.5, md: 3.5 },
} as const;

interface SectionHeaderProps {
  icon: React.ReactNode;
  /** Icon tile background and icon colour. */
  tint: string;
  color: string;
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, tint, color, title, subtitle, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '12px',
        bgcolor: tint,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': { fontSize: 22 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography component="h2" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 19, color: PROFILE_COLORS.ink, lineHeight: 1.25 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 13, color: PROFILE_COLORS.muted }}>{subtitle}</Typography>
      )}
    </Box>
    {action}
  </Box>
);
